import React, { useEffect, useState, useMemo } from 'react';
import {
    Box, Typography, Paper, IconButton, CircularProgress, TextField,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    useMediaQuery, useTheme, MenuItem, Select, Button
} from '@mui/material';
import { FiArrowLeft, FiCheckSquare, FiEdit2, FiTrash2, } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import {
    useReactTable,
    getCoreRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    getFilteredRowModel,
    flexRender
} from '@tanstack/react-table';
import api from '../../api';
import EditSetQuestionsModal from './EditSetQuestionsModal';
import EditQuestionSetModalOfManageQuestionSets from './EditQuestionSetModalOfManageQuestionSets';

const ManageQuestionSets = () => {
    const [questionSets, setQuestionSets] = useState([]);
    const [loading, setLoading] = useState(false);
    const [columnFilters, setColumnFilters] = useState([]);
    const [sorting, setSorting] = useState([]);

    const theme = useTheme();
    const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
    const navigate = useNavigate();

    const [editModalOpen, setEditModalOpen] = useState(false);
    const [selectedSet, setSelectedSet] = useState(null);

    const [selectedExam, setSelectedExam] = useState(null);


    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [selectedSetId, setSelectedSetId] = useState(null);



    const fetchQuestionSets = async () => {
        try {
            setLoading(true);
            const response = await api.get('/practice-sets'); // token auto-added
            setQuestionSets(Array.isArray(response.data.data) ? response.data.data : []);

        } catch (err) {
            console.error('Error fetching question sets:', err);
            Swal.fire('Error', 'Failed to load question sets', 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchQuestionSets();
    }, []);

    const handleDelete = async (id) => {
        const confirm = await Swal.fire({
            icon: 'warning',
            title: 'Are you sure?',
            text: 'You want to delete this question set?',
            showCancelButton: true,
            confirmButtonText: 'Yes, delete it!',
        });

        if (confirm.isConfirmed) {
            try {
                await api.delete(`/practice-sets/${id}`);
                Swal.fire('Deleted!', 'Question set deleted successfully.', 'success');
                fetchQuestionSets();
            } catch (err) {
                console.error(err);
                Swal.fire('Error', 'Failed to delete question set', 'error');
            }
        }
    };

    const SortableHeader = ({ column, title }) => (
        <Box
            sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
            onClick={column.getToggleSortingHandler()}
        >
            {title}
            {column.getIsSorted() === 'asc' ? ' 🔼' : column.getIsSorted() === 'desc' ? ' 🔽' : ''}
        </Box>
    );

    const columns = [
        {
            header: 'Sr No',
            cell: ({ row, table }) =>
                table.getState().pagination.pageIndex * table.getState().pagination.pageSize + row.index + 1,
        },

        { accessorKey: 'exam.title', header: 'Exam Name' },
        { accessorKey: 'subject.name', header: 'Subject Name' },
        { accessorKey: 'topic.title', header: 'Topic Name' },
        { accessorKey: 'name', header: 'Question Set Name' },
        { accessorKey: 'total_questions', header: 'Total Questions' },
        { accessorKey: 'total_marks', header: 'Total Marks' },
        { accessorKey: 'passing_marks', header: 'Passing Marks' },
        { accessorKey: 'duration_minutes', header: 'Duration (min)' },
        {
            id: 'actions',
            header: 'Actions',
            cell: ({ row }) => (
                <Box display="flex" gap={1}>
                    <IconButton
                        color="primary"
                        onClick={() => {
                            setSelectedSetId(row.original.id);        // Correct: set selected set ID
                            setSelectedExam(row.original.exam?.id);
                            setIsEditModalOpen(true);
                        }}
                    >
                        <FiEdit2 />
                    </IconButton>
                    <IconButton
                        color="error"
                        onClick={() => handleDelete(row.original.id)}
                    >
                        <FiTrash2 />
                    </IconButton>
                </Box>
            ),
        }
    ];

    const table = useReactTable({
        data: questionSets,
        columns,
        state: {
            columnFilters,
            sorting,
        },
        onColumnFiltersChange: setColumnFilters,
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getRowId: row => row.id, // ensure proper row id mapping
    });

    const pageIndex = table.getState().pagination.pageIndex;
    const pageSize = table.getState().pagination.pageSize;

    return (
        <Box p={2}>
            {/* Header */}
            <Box
                mb={3}
                display="flex"
                flexDirection={isSmall ? 'column' : 'row'}
                justifyContent="space-between"
                alignItems="center"
                gap={2}
                textAlign={isSmall ? 'center' : 'left'}
            >
                <IconButton
                    onClick={() => navigate(-1)}
                    sx={{
                        borderRadius: '12px',
                        p: '8px 12px',
                        background: 'linear-gradient(135deg, #00bcd4, #007bff)',
                        color: '#fff',
                        '&:hover': {
                            background: 'linear-gradient(135deg, #007bff, #00bcd4)',
                            transform: 'scale(1.05)'
                        }
                    }}
                >
                    <FiArrowLeft />
                    {!isSmall && <Typography variant="button" sx={{ ml: 1, fontWeight: 600 }}>Back</Typography>}
                </IconButton>

                <Box display="flex" alignItems="center" justifyContent="center" gap={1} flex={1}>
                    <Box sx={{
                        fontSize: '2rem',
                        background: '#e3f2fd',
                        borderRadius: '50%',
                        p: 1,
                        color: '#1976d2',
                        animation: 'pulseIcon 1.6s infinite',
                    }}>
                        <FiCheckSquare />
                    </Box>
                    <Typography
                        variant={isSmall ? 'h6' : 'h5'}
                        sx={{
                            fontWeight: 'bold',
                            background: 'linear-gradient(90deg, #007BFF, #00d4ff)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            textTransform: 'uppercase',
                            letterSpacing: '1.2px',
                            px: 1
                        }}
                    >
                        Manage Question Sets
                    </Typography>
                </Box>
            </Box>

            {/* Table */}
            <Paper elevation={3}>
                {loading ? (
                    <Box p={3} display="flex" justifyContent="center">
                        <CircularProgress />
                    </Box>
                ) : (
                    <Box>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    {table.getHeaderGroups().map(headerGroup => (
                                        <TableRow key={headerGroup.id}>
                                            {headerGroup.headers.map(header => (
                                                // <TableCell key={header.id}>
                                                //     {flexRender(header.column.columnDef.header, header.getContext())}
                                                //     {header.column.getCanFilter() && (
                                                //         <TextField
                                                //             size="small"
                                                //             variant="standard"
                                                //             placeholder="Filter..."
                                                //             value={header.column.getFilterValue() ?? ''}
                                                //             onChange={(e) => header.column.setFilterValue(e.target.value)}
                                                //             sx={{ mt: 1 }}
                                                //         />
                                                //     )}
                                                // </TableCell>

                                                <TableCell key={header.id}>
                                                    <Box display="flex" flexDirection="column" gap={0.5}>
                                                        {flexRender(header.column.columnDef.header, header.getContext())}
                                                        {header.column.getCanFilter() && (
                                                            <TextField
                                                                size="small"
                                                                variant="standard"
                                                                placeholder="Filter..."
                                                                value={header.column.getFilterValue() ?? ''}
                                                                onChange={(e) => header.column.setFilterValue(e.target.value)}
                                                                sx={{ width: '100%' }}
                                                            />
                                                        )}
                                                    </Box>
                                                </TableCell>

                                            ))}
                                        </TableRow>
                                    ))}
                                </TableHead>

                                <TableBody>
                                    {table.getRowModel().rows.map(row => (
                                        <TableRow key={row.id} hover>
                                            {row.getVisibleCells().map(cell => (
                                                <TableCell key={cell.id}>
                                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))}
                                </TableBody>

                            </Table>
                        </TableContainer>

                        <Box display="flex" justifyContent="space-between" alignItems="center" p={2} flexWrap="wrap" gap={2}>
                            <Select
                                size="small"
                                value={pageSize}
                                onChange={(e) => table.setPageSize(Number(e.target.value))}
                            >
                                {[5, 10, 20].map(size => (
                                    <MenuItem key={size} value={size}>
                                        Show {size}
                                    </MenuItem>
                                ))}
                            </Select>

                            <Box display="flex" gap={1}>
                                <Button
                                    variant="outlined"
                                    onClick={() => table.previousPage()}
                                    disabled={!table.getCanPreviousPage()}
                                >
                                    Previous
                                </Button>
                                <Button
                                    variant="outlined"
                                    onClick={() => table.nextPage()}
                                    disabled={!table.getCanNextPage()}
                                >
                                    Next
                                </Button>
                            </Box>
                        </Box>

                    </Box>
                )}
            </Paper>
            {/* <EditQuestionSetModalOfManageQuestionSets
                open={editModalOpen}
                onClose={() => setEditModalOpen(false)}
                onSave={async (updatedData) => {
                    try {
                        await axios.put(`${API_URL}/api/practice-sets/${updatedData.id}`, updatedData);
                        Swal.fire('Success', 'Question set updated successfully', 'success');
                        setEditModalOpen(false);
                        fetchQuestionSets(); // Refresh the list after update
                    } catch (err) {
                        console.error(err);
                        Swal.fire('Error', 'Failed to update question set', 'error');
                    }
                }}
                data={selectedSet}
            /> */}

            <EditSetQuestionsModal
                open={isEditModalOpen}
                onClose={() => setIsEditModalOpen(false)}
                questionSetId={selectedSetId}
                selectedExam={selectedExam}
                onSave={fetchQuestionSets}
            />
        </Box>
    );
};

export default ManageQuestionSets;
